import type { MultiLocationSuggestOption } from '@property.com.au/ui-kit';
import {
  mediaHelper,
  MultiLocationSuggest,
  paletteHelper,
  radiusHelper,
  SearchMd,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { useRef, type FunctionComponent } from 'react';
import styled from 'styled-components';
import { SEARCH_DIALOG_PADDING } from '../../../SearchModal/SearchModal';
import { DRAWN_AREA, MAP_AREA } from '../CommuteAndLocationModalLocationScreen';

const SEARCH_ICON_WIDTH_PLUS_GAPS = '3rem';

const SearchPageFormLabel = styled.span`
  ${typographyHelper('subtitle01')}
  font-weight: 500;
  font-size: 1rem;
`;

const StyledMultiLocationSuggest = styled(MultiLocationSuggest)`
  margin-bottom: ${spacingHelper('small')};
  .pill {
    background-color: ${paletteHelper('backgroundSecondary')};
    border-radius: ${radiusHelper('medium')};
  }

  .input {
    &::placeholder {
      ${typographyHelper('body01')};
      color: ${paletteHelper('textSecondary')};
    }
  }

  .scrollableArea {
    ${mediaHelper('mobile')} {
      max-width: calc(100vw - ${SEARCH_DIALOG_PADDING});
    }
  }

  .contentWrapper {
    ${mediaHelper('mobile')} {
      max-width: calc(100vw - ${SEARCH_DIALOG_PADDING} - ${SEARCH_ICON_WIDTH_PLUS_GAPS});
    }
  }
`;

interface LocationScreenMultiLocationSuggestProps {
  locationState: MultiLocationSuggestOption[];
  setLocationState: React.Dispatch<React.SetStateAction<MultiLocationSuggestOption[]>>;
}

export const LocationScreenMultiLocationSuggest: FunctionComponent<
  LocationScreenMultiLocationSuggestProps
> = ({ locationState, setLocationState }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <StyledMultiLocationSuggest
      selectedOptions={locationState}
      onSelectedOptionsChange={(options: MultiLocationSuggestOption[]) => {
        const filteredOptions = options.filter(
          (option) => option.display.text !== MAP_AREA && option.display.text !== DRAWN_AREA,
        );
        setLocationState(filteredOptions);
        setTimeout(() => {
          // Automatic scroll to the bottom of the input box when the selected options change.
          inputRef.current?.scrollIntoView({ block: 'center' });
        }, 0);
      }}
      label={<SearchPageFormLabel>Location</SearchPageFormLabel>}
      requestSource="reax-search-page"
      disableListboxFlip
      icon={<SearchMd />}
      id="SearchPageLocationSuggest"
      ref={inputRef}
      locationTypes={['suburb', 'postcode', 'region', 'state']}
      placeholder="Add a location"
      dangerouslySetClassNames={{
        listbox: 'listbox',
        pill: 'pill',
        input: 'input',
        scrollableArea: 'scrollableArea',
        contentWrapper: 'contentWrapper',
      }}
    />
  );
};
